import axios from "axios"
import { AssignSubscriptionRequest, CreateSubscriptionRequest, DateRange, ExactWebHookSubscriptionRequest, IAddTenantRequest, IAdminStatusUpdate, IBoundField, IBulkUpdate, IDatasource, IDatasourceAuthDto, IDatasourceRequestDto, IDatasourceType, IDocumentLibrary, IPartnerProfile, IPortalPartner, IPortalTenantRequestFeedback, IPortalUser, IRequest, ISite, ITenant, ITenantContextUsage, ITenantUser, IUserProfileRequest, MultiTenantSubscriptionRequest, SubscriptionPriceTierResponse, SubscriptionStatusUpdateRequest, TenantApiKeyUpdateRequest, UserDeleteRequest } from "../../data-structures/interfaces";
import { Client, ResponseType } from "@microsoft/microsoft-graph-client/lib/src/browser";
import { ErrorHelper } from "./ErrorHelper";
import { apiHeaders, axiosDelete, axiosGet, axiosPost, axiosPut } from "./AxiosUtil";
import { AuthHelper, IJwtAuthTokenResult } from "./AuthHelper";
import { SubscriptionIntervalEnum, SubscriptionProductEnum } from "../../data-structures/enums";
import { isUnlimitedSubscription } from "./SubscriptionCapacityHelper";

export const setAuthToken = async (profileRequest: IUserProfileRequest) => {
    try {
        const authToken: IJwtAuthTokenResult = (await axios.post(`${process.env.REACT_APP_API_URL}Auth/GetAuthToken`, profileRequest, {
            headers: {
                [apiHeaders.key.xDocubirdAuth]: apiHeaders.value.xDocubirdAuth
            }
        }))?.data;

        if (authToken) {
            AuthHelper.setDocubirdToken(authToken);
            return true;
        }
    } catch (e) {
        ErrorHelper.handle(e);
    }
    return false;
}

export const refreshJwt = async (accessToken: string) => {
    try {
        // Use default axios instance to skip access token refresh interceptor
        const result: IJwtAuthTokenResult = (await axios.get(
            `${process.env.REACT_APP_API_URL}auth/RefreshJwt`,
            {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                    [apiHeaders.key.xDocubirdAuth]: apiHeaders.value.xDocubirdAuth
                }
            }
        ))?.data;
        return result;
    } catch (e) {
        ErrorHelper.handle(e);
    }
    return null;
}

export const getCurrentProfile = async (profileRequest: IUserProfileRequest) => {
    try {
        const currentProfile: IPortalUser = (await axiosPost(`User/GetProfile`, profileRequest))?.data;
        return currentProfile;
    } catch (e) {
        ErrorHelper.handle(e);
        return null;
    }
}

export const saveUserChanges = async (user: ITenantUser) => {
    try {
        await axiosPut(`User/Update/`, user);
    } catch (e) {
        ErrorHelper.handle(e);
        return null;
    }
}

export const getPartnerProfile = async (partnerId: number) => {
    try {
        const partnerProfile: IPartnerProfile = (await axiosGet(`Partner/GetProfile/${partnerId}`))?.data;
        return partnerProfile;
    } catch (e) {
        ErrorHelper.handle(e);
        return null;
    }
}

export const updateTenant = async (tenant: ITenant) => {
    try {
        await axiosPut(`Tenant/Update`, tenant);
    } catch (e) {
        ErrorHelper.handle(e);
        return null;
    }
}

export const updateTenantApiKey = async (request: TenantApiKeyUpdateRequest) => {
    try {
        await axiosPut(`tenant/updateapikey`, request);
        return true;
    } catch (e) {
        ErrorHelper.handle(e);
        return false;
    }
}

export const removeTenant = async (partnerId: number, tenant: ITenant) => {
    try {
        await axiosPut(`Partner/RemoveTenant/${partnerId}`, { tenantId: tenant.tenantId });
    } catch (e) {
        ErrorHelper.handle(e);
        return null;
    }
}

export const bulkUpdates = async (bulkUpdates: IBulkUpdate) => {
    try {
        await axiosPut(`User/BulkUpdate/`, bulkUpdates);
    } catch (e) {
        ErrorHelper.handle(e);
        return null;
    }
}

export const updateAdminStatus = async (bulkUpdates: IAdminStatusUpdate) => {
    try {
        await axiosPut(`User/UpdateAdminStatus/`, bulkUpdates);
    } catch (e) {
        ErrorHelper.handle(e);
        return null;
    }
}

export const removeTenantsAPI = async (partnerId: number, tenantIds: number[]) => {
    try {
        await axiosPut(`Partner/RemoveTenants/${partnerId}`, tenantIds);
    } catch (e) {
        ErrorHelper.handle(e);
        return null;
    }
}

export const addTenantAPI = async (tenant: ITenant) => {

    const addTenantRequest: IAddTenantRequest = {
        ...tenant,
        allowedAdvancedSearchFields: tenant.allowedAdvancedSearchFields ? (tenant.allowedAdvancedSearchFields as string[]).map(x => x).join(";") : "",
        allowedSiteCollections: tenant.allowedSiteCollections ? (tenant.allowedSiteCollections as string[]).map(x => x).join(";") : "",
        allowedTeams: tenant.allowedTeams ? (tenant.allowedTeams as string[]).map(x => x).join(";") : "",
        hiddenMetadataFields: tenant.hiddenMetadataFields ? (tenant.hiddenMetadataFields as string[]).map(x => x).join(";") : "",
    }

    try {
        const newTenant: ITenant = (await axiosPost(`Tenant/AddTenant`, addTenantRequest))?.data;
        return {
            success: true,
            tenant: newTenant
        }
    } catch (e) {
        ErrorHelper.handle(e);
        return {
            success: false,
            message: e.response?.data
        }
    }
}

export const assignTenantsAPI = async (partnerId: number, tenantIds: number[]) => {
    try {
        await axiosPut(`Partner/AssignTenants/${partnerId}`, tenantIds);
    } catch (e) {
        ErrorHelper.handle(e);
        return null;
    }
}

export const assignPartnerUsers = async (partnerId: number, userIds: number[]) => {
    try {
        await axiosPut(`Partner/AssignUsers/${partnerId}`, userIds);
    } catch (e) {
        ErrorHelper.handle(e);
        return null;
    }
}

export const removePartnerUsers = async (partnerId: number, userIds: number[]) => {
    try {
        await axiosPut(`Partner/RemoveUsers/${partnerId}`, userIds);
    } catch (e) {
        ErrorHelper.handle(e);
        return null;
    }
}

export const getPartners = async () => {
    try {
        const partners = (await axiosGet(`Partner`))?.data as IPortalPartner[];
        return partners;
    } catch (e) {
        ErrorHelper.handle(e);
        return null;
    }
}

export const createPartner = async (partner: IPortalPartner) => {
    try {
        const newPartner = (await axiosPost(`Partner/Add`, partner))?.data as IPortalPartner;
        return newPartner;
    } catch (e) {
        ErrorHelper.handle(e);
        return null;
    }
}

export const updatePartner = async (partnerId: number, name: string) => {
    try {
        await axiosPut(`Partner/Edit`, { id: partnerId, name });
    } catch (e) {
        ErrorHelper.handle(e);
        return null;
    }
}

export const deletePartner = async (partnerId: number) => {
    try {
        await axiosDelete(`Partner/Delete/${partnerId}`,);
    } catch (e) {
        ErrorHelper.handle(e);
        return null;
    }
}

export const requestTenants = async (userId: number, partnerId: number, requestedTenants: ITenant[]) => {
    try {
        await axiosPost(`Partner/requesttenants/${userId}/${partnerId}`, requestedTenants);
    } catch (e) {
        ErrorHelper.handle(e);
        return e.response?.data?.error;
    }
}

export const loadRequests = async () => {
    try {
        const result: IRequest[] = (await axiosGet(`tenantrequest/load`))?.data;
        return result
    } catch (e) {
        ErrorHelper.handle(e);
        return null;
    }
}

export const updateRequest = async (requestFeedback: IPortalTenantRequestFeedback) => {
    try {
        await axiosPut(`tenantrequest/update`, requestFeedback);
    } catch (e) {
        ErrorHelper.handle(e);
        return null;
    }
}

export const deleteTenant = async (tenantId: number) => {
    try {
        await axiosDelete(`tenant/delete/${tenantId}`);
    } catch (e) {
        ErrorHelper.handle(e);
        return null;
    }
}

export const getTenantSites = async (tenantId: number) => {
    try {
        const sites: ISite[] = (await axiosGet(`tenant/sites/${tenantId}`))?.data;
        const mappedSites: ISite[] = sites?.map(site => {
            return ({
                ...site,
                documentLibraries: null,
            })
        })
        return mappedSites;
    } catch (e) {
        ErrorHelper.handle(e);
        return null;
    }
}

export const getTenantContextUsage = async (tenantId: number): Promise<ITenantContextUsage[] | null> => {
    try {
        const usage: ITenantContextUsage[] = (await axiosGet(`tenant/${tenantId}/contextusage`))?.data;
        return usage ?? [];
    } catch (e) {
        ErrorHelper.handle(e);
        return null;
    }
}

export const addTenantSites = async (tenantId: number, sites: ISite[]) => {
    try {
        //remove libs from sites
        const processedSites = [...sites].map(site => {
            delete site.documentLibraries;
            return site;
        })
        const updatedSites = (await axiosPost(`tenant/addsites/${tenantId}`, processedSites));
        return updatedSites?.data;
    } catch (e) {
        ErrorHelper.handle(e);
        return null;
    }
}

export const clearTenantUserEmailMappings = async (tenantId: string) => {
    try {
        await axiosPut(`tenant/clearuseremailmappings/${tenantId}`);
    } catch (e) {
        ErrorHelper.handle(e);
    }
}

export const deleteSite = async (siteIds: number[]) => {
    try {
        await axiosPut(`site/delete`, siteIds);
    } catch (e) {
        ErrorHelper.handle(e);
    }
}

export const getSiteLibraries = async (siteId: number) => {
    try {
        const libraries: IDocumentLibrary[] = (await axiosGet(`site/documentlibraries/${siteId}`))?.data;
        return libraries;
    } catch (e) {
        ErrorHelper.handle(e);
        return null;
    }
}

export const addSiteLibrary = async (siteId: number, libraries: IDocumentLibrary[]) => {
    try {
        const updatedLibrariesCall = (await axiosPost(`site/adddocumentlibraries/${siteId}`, libraries));
        const updatedLibraries: IDocumentLibrary[] = updatedLibrariesCall?.data;
        return updatedLibraries;
    } catch (e) {
        ErrorHelper.handle(e);
        return null;
    }
}

export const deleteLibrary = async (libIds: number[]) => {
    try {
        await axiosPut(`documentlibrary/delete`, libIds);
    } catch (e) {
        ErrorHelper.handle(e);
    }
}

export const updateLibrary = async (libraries: IDocumentLibrary[]) => {
    try {
        axiosPut(`documentlibrary/update`, libraries);
    } catch (e) {
        ErrorHelper.handle(e);
    }
}

export const updateSite = async (sites: ISite[]) => {
    try {
        const processedSites = [...sites].map(site => {
            delete site.documentLibraries;
            return site;
        })
        axiosPut(`site/update`, processedSites);
    } catch (e) {
        ErrorHelper.handle(e);
    }
}

export const getTenantDatasource = async (tenantId: number): Promise<IDatasource[]> => {
    try {
        const datasources = (await axiosGet(`tenant/${tenantId}/datasources`))?.data;
        return datasources ?? [];
    } catch (err) {
        ErrorHelper.handle(err);
        return [];
    }
}

export const getDatasourceStructure = async (requestDto: IDatasourceRequestDto): Promise<string[]> => {
    try {
        const datasourceStructure = (await axiosPost(`datasource/getStructure`, requestDto))?.data;
        return datasourceStructure;
    } catch (err) {
        ErrorHelper.handle(err);
        return null;
    }
}

export const createNewDatasource = async (datasource: IDatasource): Promise<IDatasource> => {
    try {
        const datasourceStructure = (await axiosPost(`datasource/add`, datasource))?.data;
        return datasourceStructure;
    } catch (err) {
        ErrorHelper.handle(err);
        return null;
    }
}

export const editDatasource = async (datasource: IDatasource): Promise<number> => {
    try {
        const res = (await axiosPut(`datasource/edit`, datasource));
        return res.status;
    } catch (err) {
        ErrorHelper.handle(err);
        return null;
    }
}

export const resetDatasourceCache = async (datasource: IDatasource): Promise<number> => {
    try {
        const res = (await axiosPut(`datasource/resetcache`, datasource));
        return res.status;
    } catch (err) {
        ErrorHelper.handle(err);
        return null;
    }
}

export const deleteDatasource = async (datasourceId: number) => {
    try {
        const res = await axiosDelete(`datasource/delete/${datasourceId}`);
        return res.status;
    } catch (err) {
        ErrorHelper.handle(err);
        return null;
    }
}

export const createNewBoundField = async (boundfield: IBoundField): Promise<IBoundField> => {
    try {
        const field = (await axiosPost(`datasource/boundfield/add`, boundfield))?.data;
        return field;
    } catch (err) {
        ErrorHelper.handle(err);
        return null;
    }
}

export const updateBoundFields = async (boundfields: IBoundField[]): Promise<number> => {
    try {
        const res = await axiosPut(`datasource/boundfields/update`, boundfields);
        return res.status;
    } catch (err) {
        ErrorHelper.handle(err);
        return null;
    }
}

export const deleteBoundField = async (id: number): Promise<number> => {
    try {
        const res = await axiosDelete(`datasource/boundfield/delete/${id}`);
        return res.status;
    } catch (err) {
        ErrorHelper.handle(err);
        return null;
    }
}

export const getDatasourceTypes = async (): Promise<IDatasourceType[]> => {
    try {
        const types = (await axiosGet(`datasource/types`))?.data;
        return types;
    } catch (err) {
        ErrorHelper.handle(err);
        return null;
    }
}

const getAuthenticatedClient = (accessToken: string) => {
    return Client.init({
        authProvider: (done: any) => {
            done(null, accessToken);
        }
    });
}

export const getListStructure = async (accessToken: string, siteUrl: string, listTitle: string) => {
    try {
        const siteUrlObject = new URL(siteUrl);
        const client = getAuthenticatedClient(accessToken);
        const siteResponse = await client
            .api(`/sites/${siteUrlObject.hostname}:/${siteUrlObject.pathname}`)
            .get();

        if (siteResponse?.id) {
            await client
                .api(`/sites/${siteResponse.id}/lists/${listTitle}`)
                .expand("columns")
                .get();
        }
    }
    catch (e) {
        ErrorHelper.handle(e);
    }
    return null;
}

export const getUserProfilePicture = async (accessToken: string) => {
    try {
        const client = getAuthenticatedClient(accessToken);
        const response = await client
            .api("me/photos/48x48/$value")
            .header("Cache-Control", "no-cache")
            .responseType(ResponseType.RAW)
            .get();

        if (response.ok) {
            const url = window.URL || window.webkitURL;
            const blob = await response.blob();
            const imageUrl = url.createObjectURL(blob);
            return imageUrl;
        }
    }
    catch (e) {
        ErrorHelper.handle(e);
    }
    return null;
}

const createClientOperationId = () =>
    typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
        ? crypto.randomUUID()
        : "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, c => {
            const r = Math.floor(Math.random() * 16);
            const v = c === "x" ? r : (r & 0x3) | 0x8;
            return v.toString(16);
        });

export const createSubscription = async (request: CreateSubscriptionRequest) => {
    try {
        const isStripe = request.method?.toLowerCase() === "stripe";
        const payload = isStripe && !request.clientOperationId
            ? { ...request, clientOperationId: createClientOperationId() }
            : request;
        const field = (await axiosPost(`subscriptions/create`, payload))?.data;
        return field;
    } catch (err) {
        ErrorHelper.handle(err);
        return null;
    }
}

const transformSubscriptionData = (sub, languageStrings, tenants = null) => {
    const licenseCount = typeof sub.licenseCount === "number" ? sub.licenseCount : null;
    const availableLicenseCount = typeof sub.availableLicenses === "number" ? sub.availableLicenses : null;
    const assignedLicenseCount = typeof sub.assignedLicenses === "number" ? sub.assignedLicenses : 0;
    const productId = typeof sub.productId === "number" ? sub.productId : null;
    const isServiceProduct = productId === SubscriptionProductEnum.Service;
    // Service add-ons have no seats — never treat them as "unlimited" (that would render an
    // "Unlimited available" label and imply they're assignable).
    const isUnlimitedLicenses = !isServiceProduct && isUnlimitedSubscription({
        method: sub.method,
        tier: sub.tier,
        licenseCount
    });

    let availableLicensesText = "-";
    if (!isUnlimitedLicenses && !isServiceProduct) {
        availableLicensesText = `${availableLicenseCount ?? 0} of ${licenseCount ?? 0}`;
    }

    let assignedLicensesText = assignedLicenseCount ? assignedLicenseCount.toString() : "-";
    let intervalText = "";

    switch (sub.interval) {
        case SubscriptionIntervalEnum.OnceOff:
            intervalText = languageStrings.OnceOff;
            break;
        case SubscriptionIntervalEnum.Monthly:
            intervalText = languageStrings.Monthly;
            break;
        case SubscriptionIntervalEnum.Yearly:
            intervalText = languageStrings.Yearly;
            break;
        default:
            break;
    }

    let expiryDateText = "-";
    if (sub.expiryDate) {
        expiryDateText = new Date(sub.expiryDate).toLocaleString();
    }

    let paymentPlatform: string;
    if (sub.method?.toLowerCase()?.startsWith("invoice")) {
        paymentPlatform = languageStrings.Invoice;
    }
    else if (sub.method?.toLowerCase()?.startsWith("marketplace")) {
        paymentPlatform = "Azure Marketplace";
    }
    else if (sub.method?.toLowerCase() === "stripe") {
        paymentPlatform = "Stripe";
    }

    const transformedSub = {
        ...sub,
        status: sub.status?.length > 1 ? `${sub.status.slice(0, 1).toUpperCase()}${sub.status.slice(1)}` : sub.status,
        licenseCount,
        availableLicenseCount,
        assignedLicenseCount,
        isUnlimitedLicenses,
        availableLicenses: availableLicensesText,
        interval: intervalText,
        assignedLicenses: assignedLicensesText,
        expiryDateRaw: sub.expiryDate ? new Date(sub.expiryDate) : null,
        expiryDate: expiryDateText,
        cancelAtRaw: sub.cancelAt ? new Date(sub.cancelAt) : null,
        amountString: sub.amountString ? sub.amountString : "-",
        paymentPlatform: paymentPlatform ? paymentPlatform : "-"
    };

    if (tenants) {
        transformedSub.tenantName = tenants.find(x => x.id === sub.tenantId)?.friendlyName || "-";
    }

    return transformedSub;
};

export const getTenantSubscriptions = async (tenantId: number, languageStrings: any, tenants?: ITenant[]): Promise<any[]> => {
    try {
        const subscriptions = (await axiosGet(`subscriptions/tenant/${tenantId}`))?.data;
        return subscriptions.map(sub => transformSubscriptionData(sub, languageStrings, tenants));
    } catch (err) {
        ErrorHelper.handle(err);
        return [];
    }
}

export const getSubscriptionById = async (
    subscriptionId: number,
    languageStrings: any,
    tenants?: ITenant[]
): Promise<any | null> => {
    try {
        const sub = (await axiosGet(`subscriptions/${subscriptionId}`))?.data;
        if (!sub) return null;
        return transformSubscriptionData(sub, languageStrings, tenants);
    } catch (err) {
        ErrorHelper.handle(err);
        return null;
    }
}

export const getMultiTenantSubscriptions = async (request: MultiTenantSubscriptionRequest, tenants: ITenant[], languageStrings: any): Promise<any[]> => {
    try {
        const subscriptions = (await axiosPost(`subscriptions/multi`, request))?.data;
        return subscriptions.map(sub => transformSubscriptionData(sub, languageStrings, tenants));
    } catch (err) {
        ErrorHelper.handle(err);
        return [];
    }
}

export const getTenantSubscriptionsPayments = async (tenantId: number) => {
    try {
        const data = (await axiosGet(`subscriptions/payments/tenant/${tenantId}`))?.data;

        return data;
    } catch (err) {
        ErrorHelper.handle(err);
        return [];
    }
}

export const assignUsersToSubscription = async (request: AssignSubscriptionRequest) => {
    try {
        const results = (await axiosPost(`subscriptions/users/assign`, request))?.data;
        return results;
    } catch (err) {
        ErrorHelper.handle(err);
        return null;
    }
}

export const assignAllUsersToSubscription = async (request: AssignSubscriptionRequest): Promise<number[]> => {
    try {
        const results = (await axiosPost(`subscriptions/users/assignall`, request))?.data;
        return results;
    } catch (err) {
        ErrorHelper.handle(err);
        return null;
    }
}

export const revokeUsersFromSubscription = async (request: AssignSubscriptionRequest) => {
    try {
        const results = (await axiosPost(`subscriptions/users/revoke`, request))?.data;
        return results;
    } catch (err) {
        ErrorHelper.handle(err);
        return null;
    }
}

export const revokeAllUsersFromSubscription = async (subscriptionId: number) => {
    try {
        const results = (await axiosDelete(`subscriptions/users/revokeall/${subscriptionId}`))?.data;
        return results;
    } catch (err) {
        ErrorHelper.handle(err);
        return null;
    }
}

export const cancelSubscription = async (subscriptionId: number, clientOperationId?: string) => {
    try {
        // For Stripe cancellations the backend accepts an optional clientOperationId via query string;
        // for legacy paths the parameter is simply ignored.
        const url = clientOperationId
            ? `subscriptions/cancel/${subscriptionId}?clientOperationId=${encodeURIComponent(clientOperationId)}`
            : `subscriptions/cancel/${subscriptionId}`;
        const results = (await axiosDelete(url))?.data;
        return results;
    } catch (err) {
        // Run shared side-effects (console + 401 sign-out) and then rethrow so the
        // caller can distinguish success from failure. Silently returning null here
        // would let the UI close the cancel dialog as if it had succeeded.
        ErrorHelper.handle(err);
        throw err;
    }
}

export const reactivateSubscription = async (subscriptionId: number, clientOperationId?: string) => {
    try {
        // Mirrors cancelSubscription: clientOperationId passed as a query string for
        // idempotency. The server generates one if missing, but the frontend always
        // supplies its own so a retry of the same logical click is deduped end-to-end.
        const opId = clientOperationId ?? createClientOperationId();
        const url = `subscriptions/reactivate/${subscriptionId}?clientOperationId=${encodeURIComponent(opId)}`;
        const result = (await axiosPost(url, {}))?.data;
        return result;
    } catch (err) {
        ErrorHelper.handle(err);
        throw err;
    }
}

export const getStripeCatalog = async (): Promise<import("../../data-structures/interfaces").StripeCatalogProductDto[]> => {
    try {
        const data = (await axiosGet(`subscriptions/stripe-catalog`))?.data;
        return Array.isArray(data) ? data : [];
    } catch (err) {
        ErrorHelper.handle(err);
        return [];
    }
}

// Stripe product catalog admin (Manage Stripe Products panel).
// The endpoints use the standardized errorOrData shape from MapResult on the controller;
// failures throw so callers can distinguish 400/409/404 via the error response body.

export type StripeProductMutationResult = {
    product: import("../../data-structures/interfaces").StripeProductDto;
    warning?: string;
}

const extractWarning = (response: any): string | undefined => {
    // Server surfaces non-blocking warnings in a response header so the body stays a
    // clean DTO. Header names are case-insensitive in axios.
    const h = response?.headers?.["x-stripe-catalog-warning"]
        ?? response?.headers?.["X-Stripe-Catalog-Warning"];
    return typeof h === "string" && h.length > 0 ? h : undefined;
}

export const listStripeProducts = async (): Promise<import("../../data-structures/interfaces").StripeProductDto[]> => {
    try {
        const data = (await axiosGet(`stripeproducts`))?.data;
        return Array.isArray(data) ? data : [];
    } catch (err) {
        ErrorHelper.handle(err);
        return [];
    }
}

export const createStripeProduct = async (
    body: import("../../data-structures/interfaces").UpsertStripeProductRequest
): Promise<StripeProductMutationResult> => {
    const response = await axiosPost(`stripeproducts`, body);
    return { product: response?.data, warning: extractWarning(response) };
}

export const updateStripeProduct = async (
    id: number,
    body: import("../../data-structures/interfaces").UpsertStripeProductRequest
): Promise<StripeProductMutationResult> => {
    const response = await axiosPut(`stripeproducts/${id}`, body);
    return { product: response?.data, warning: extractWarning(response) };
}

export const activateStripeProduct = async (id: number): Promise<StripeProductMutationResult> => {
    const response = await axiosPost(`stripeproducts/${id}/activate`, {});
    return { product: response?.data, warning: extractWarning(response) };
}

export const deactivateStripeProduct = async (id: number): Promise<StripeProductMutationResult> => {
    const response = await axiosPost(`stripeproducts/${id}/deactivate`, {});
    return { product: response?.data, warning: extractWarning(response) };
}

export const syncStripeProductsPreview = async (
): Promise<import("../../data-structures/interfaces").StripeCatalogSyncPreview> => {
    const response = await axiosPost(`stripeproducts/sync/preview`, {});
    return response?.data;
}

export type SyncApplyOutcome =
    | { kind: "ok"; products: import("../../data-structures/interfaces").StripeProductDto[] }
    | { kind: "conflict"; freshPreview: import("../../data-structures/interfaces").StripeCatalogSyncPreview; error: string }
    | { kind: "error"; error: string };

export const syncStripeProductsApply = async (
    body: import("../../data-structures/interfaces").StripeCatalogSyncApplyRequest
): Promise<SyncApplyOutcome> => {
    try {
        const response = await axiosPost(`stripeproducts/sync/apply`, body);
        return { kind: "ok", products: response?.data };
    } catch (err: any) {
        // 409 carries the fresh preview in the response body so the UI can re-render
        // without an extra round-trip.
        const status: number | undefined = err?.response?.status;
        const data = err?.response?.data;
        if (status === 409 && data?.freshPreview) {
            return { kind: "conflict", freshPreview: data.freshPreview, error: data.error ?? "Sync conflict" };
        }
        return { kind: "error", error: data?.error ?? err?.message ?? "Sync failed" };
    }
}

export const createStripePortalSession = async (subscriptionId: number, returnUrl?: string) => {
    try {
        const body = returnUrl ? { returnUrl } : {};
        const data = (await axiosPost(`subscriptions/${subscriptionId}/portal-session`, body))?.data;
        return data;
    } catch (err) {
        ErrorHelper.handle(err);
        return null;
    }
}

export const updateStripeSubscriptionQuantity = async (
    subscriptionId: number,
    quantity: number,
    prorationBehavior: "create_prorations" | "none"
) => {
    try {
        const clientOperationId = createClientOperationId();
        const result = (await axiosPost(`subscriptions/${subscriptionId}/quantity`, {
            quantity,
            prorationBehavior,
            clientOperationId
        }))?.data;
        return result;
    } catch (err) {
        // Run shared side-effects (console + 401 sign-out) and then rethrow so the
        // caller can distinguish success from failure. Silently returning null here
        // would let the UI show the change as "submitted" when Stripe rejected it.
        ErrorHelper.handle(err);
        throw err;
    }
}

export interface QuantityChangePreview {
    totalAmount: number;
    prorationDelta: number;
    currency: string;
    prorationBehavior: "create_prorations" | "none";
}

export const previewStripeSubscriptionQuantity = async (
    subscriptionId: number,
    quantity: number,
    prorationBehavior: "create_prorations" | "none"
): Promise<QuantityChangePreview | null> => {
    try {
        const result = (await axiosPost(`subscriptions/${subscriptionId}/quantity-preview`, {
            quantity,
            prorationBehavior
        }))?.data;
        return result ?? null;
    } catch (err) {
        // Preview is best-effort UX sugar — never block the dialog if it fails.
        // ErrorHelper handles 401 sign-out; we swallow other failures and let the
        // caller show "preview unavailable" in the dialog.
        ErrorHelper.handle(err);
        return null;
    }
}

export interface SubscriptionHistoryItem {
    id: number;
    subscriptionId: number;
    eventKind: string;
    source: string;
    actorUserId?: number | null;
    actorEmail?: string | null;
    stripeEventId?: string | null;
    stripeEventType?: string | null;
    clientOperationId?: string | null;
    stripeOpVersion?: number | null;
    oldValuesJson?: string | null;
    newValuesJson?: string | null;
    detailsJson?: string | null;
    status: string;
    failureReason?: string | null;
    occurredAt: string;
}

export interface SubscriptionHistoryResponse {
    items: SubscriptionHistoryItem[];
    nextBefore: number | null;
}

export const getSubscriptionHistory = async (
    subscriptionId: number,
    before?: number | null,
    limit: number = 50
): Promise<SubscriptionHistoryResponse | null> => {
    try {
        const qs = new URLSearchParams();
        if (typeof before === "number" && before > 0) {
            qs.set("before", String(before));
        }
        if (typeof limit === "number" && limit > 0) {
            qs.set("limit", String(limit));
        }
        const suffix = qs.toString() ? `?${qs}` : "";
        const result = (await axiosGet(`subscriptions/${subscriptionId}/history${suffix}`))?.data;
        return result ?? null;
    } catch (err) {
        ErrorHelper.handle(err);
        return null;
    }
}

export interface SubscriptionPaymentItem {
    id: number;
    subscriptionId?: number | null;
    paymentId?: string | null;
    transactionId?: string | null;
    status: string;
    description?: string | null;
    amountValue: number;
    amountString?: string | null;
    method?: string | null;
    createdDate: string;
    stripeInvoiceLineItemId?: string | null;
    stripeSubscriptionItemId?: string | null;
    hostedInvoiceUrl?: string | null;
    invoicePdfUrl?: string | null;
}

export const getSubscriptionPayments = async (
    subscriptionId: number
): Promise<SubscriptionPaymentItem[] | null> => {
    try {
        const result = (await axiosGet(`subscriptions/${subscriptionId}/payments`))?.data;
        return Array.isArray(result) ? result : [];
    } catch (err) {
        ErrorHelper.handle(err);
        return null;
    }
}

// On-demand: resolve + persist the Stripe payment instrument (Payment.Method) for this
// subscription's paid payments, returning the refreshed list. Best-effort server-side;
// returns null only on a transport error (the panel then keeps the un-enriched rows).
export const resolveSubscriptionPaymentMethods = async (
    subscriptionId: number
): Promise<SubscriptionPaymentItem[] | null> => {
    try {
        const result = (await axiosPost(`subscriptions/${subscriptionId}/payments/resolve-methods`, {}))?.data;
        return Array.isArray(result) ? result : [];
    } catch (err) {
        ErrorHelper.handle(err);
        return null;
    }
}

export const updateSubscriptionStatus = async (request: SubscriptionStatusUpdateRequest) => {
    try {
        const results = (await axiosPut(`subscriptions/updatestatus`, request))?.data;
        return results;
    } catch (err) {
        ErrorHelper.handle(err);
        return null;
    }
}

export const getSubscriptionPriceTiers = async (): Promise<SubscriptionPriceTierResponse[]> => {
    try {
        const data = (await axiosGet(`subscriptions/pricetiers`))?.data;

        return data;
    } catch (err) {
        ErrorHelper.handle(err);
        return null;
    }
}

export const getExactAuthToken = async (endpoint: string, code: string, state: string) => {
    try {
        // const data = (await axiosGet(`${endpoint}?&code=${encodeURIComponent(code.trim())}&state=${encodeURIComponent(state.trim())}`))?.data;
        // return data;
        const authToken = (await axios.get(
            `${endpoint}?&code=${encodeURIComponent(code.trim())}&state=${encodeURIComponent(state.trim())}`,
            {
                headers: {
                    [apiHeaders.key.xDocubirdAuth]: apiHeaders.value.xDocubirdAuth
                }
            }))?.data;
        return authToken;
    } catch (err) {
        ErrorHelper.handle(err);
        return null;
    }
}

export const exactCreateWebHookSubscription = async (request: ExactWebHookSubscriptionRequest) => {
    try {
        const results = (await axiosPost("Exact/HookSubscription", request))?.data;
        return results;
    } catch (err) {
        console.error(err);
        throw err;
    }
}

export const exactGetWebHookSubscriptions = async (clientId: string) => {
    try {
        const results = (await axiosGet(`Exact/WebHookSubscriptions/${clientId}`))?.data;
        return results;
    } catch (err) {
        ErrorHelper.handle(err);
        return null;
    }
}

export const exactRemoveWebHookSubscription = async (datasourceId: number, subscriptionId: string) => {
    try {
        const results = (await axiosDelete(`Exact/HookUnsubscribe/${datasourceId}/${subscriptionId}`))?.data;
        return results;
    } catch (err) {
        console.error(err);
        throw err;
    }
}

export const deleteUsers = async (request: UserDeleteRequest) => {
    try {
        const results = (await axiosPut(`${process.env.REACT_APP_API_URL}user/delete`, request))?.data;
        return results;
    } catch (err) {
        ErrorHelper.handle(err);
        return null;
    }
}

export const getSubscriptionReport = async (dateRange: DateRange, userId: number) => {
    try {
        const results = (await axiosGet(`${process.env.REACT_APP_API_URL}subscriptions/report/${userId.toString()}?startDate=${dateRange.startDate}&endDate=${dateRange.endDate}`, {
            responseType: 'blob'
        }));

        return results?.data;
    } catch (err) {
        ErrorHelper.handle(err);
        return null;
    }
}

export const GetDatasourceAuthUrl = async (requestDto: IDatasourceAuthDto) => {
    try {
        // const authUrl = (await axiosPost(`Auth/GetDatasourceAuthUrl`, requestDto))?.data;
        // return authUrl;
        const authUrl = (await axios.post(
            `${process.env.REACT_APP_API_URL}Auth/GetDatasourceAuthUrl`,
            requestDto,
            {
                headers: {
                    [apiHeaders.key.xDocubirdAuth]: apiHeaders.value.xDocubirdAuth
                }
            }))?.data;
        return authUrl;
    } catch (err) {
        console.error(err);
        return null;
    }
}

export const getAdminConsentUrl = async (tid: string, sct: string) => {
    try {
        const url: string = (await axios.get(
            `${process.env.REACT_APP_API_URL}consent/consenturl/${tid}?sct=${sct}`,
            {
                headers: {
                    [apiHeaders.key.xDocubirdAuth]: apiHeaders.value.xDocubirdAuth
                }
            }))?.data;
        return url;
    } catch (err) {
        ErrorHelper.handle(err);
        return null;
    }
}

export const getOrphanCustomers = async () => {
    try {
        const result = (await axiosGet(`stripewebhook/orphans`))?.data;
        return result;
    } catch (err) {
        ErrorHelper.handle(err);
        return null;
    }
}

export const getOrphanCustomersCount = async () => {
    try {
        const result = (await axiosGet(`stripewebhook/orphans/count`))?.data;
        return result as { count: number; totalEvents: number; unresolvedEvents: number };
    } catch (err) {
        ErrorHelper.handle(err);
        return null;
    }
}

export const adoptOrphanCustomer = async (customerId: string, tenantId: number) => {
    try {
        const result = (await axiosPost(`stripewebhook/orphans/${encodeURIComponent(customerId)}/adopt`, { tenantId }))?.data;
        return result;
    } catch (err) {
        ErrorHelper.handle(err);
        return null;
    }
}

export const getAllTenantsLite = async () => {
    try {
        const result = (await axiosGet(`tenant/all`))?.data;
        return result;
    } catch (err) {
        ErrorHelper.handle(err);
        return null;
    }
}
